import React, { useState, useEffect } from "react";
import { Box, Chip, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import color from "../../Constants/colors";
import {
  useCreateMcpConnection,
  useUpdateMcpConnection,
} from "../../Query/useComoAgent";
import { getSingleMcpConnection } from "../../Api/comoAgent";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoButton } from "../Common/ComoButton";
import { ComoInput } from "../Common/ComoInput";
import { ComoSelect } from "../Common/ComoSelect";
import { useTranslation } from "react-i18next";

const McpConnectionForm = () => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const isEdit = !!connectionId;
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("mcpConnectionForm");

  const AUTH_TYPES = [
    { value: 0, label: t("none") },
    { value: 1, label: t("apiKey") },
    { value: 2, label: t("bearerToken") },
    { value: 3, label: t("basicAuth") },
  ];

  const createMutation = useCreateMcpConnection();
  const updateMutation = useUpdateMcpConnection();

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState(0);
  const [apiKey, setApiKey] = useState("");
  const [headerName, setHeaderName] = useState(isEdit ? "" : "X-API-Key");
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cachedTools, setCachedTools] = useState<{ name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEdit && connectionId) {
      setIsLoading(true);
      getSingleMcpConnection(Number(connectionId))
        .then((conn) => {
          setName(conn.name);
          setUrl(conn.url);
          setAuthType(conn.auth_type);
          setCachedTools(conn.cached_tools || []);
        })
        .finally(() => setIsLoading(false));
    }
  }, [connectionId, isEdit]);

  const omitBlank = (values: Record<string, string>) =>
    Object.fromEntries(
      Object.entries(values).filter(([, value]) => value.trim() !== ""),
    );

  const buildCredentials = (): Record<string, string> => {
    switch (authType) {
      case 1:
        return omitBlank({ api_key: apiKey, header_name: headerName });
      case 2:
        return omitBlank({ token });
      case 3:
        return omitBlank({ username, password });
      default:
        return {};
    }
  };

  const handleSubmit = () => {
    if (isEdit) {
      updateMutation.mutate(
        {
          id: Number(connectionId),
          data: {
            name,
            url,
            auth_type: authType,
            credentials: buildCredentials(),
          },
        },
        { onSuccess: () => navigate("/mcp_connections") },
      );
      return;
    }

    createMutation.mutate(
      {
        name,
        url,
        auth_type: authType,
        credentials: buildCredentials(),
      },
      { onSuccess: () => navigate("/mcp_connections") },
    );
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/mcp_connections")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <HubOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {isEdit ? t("editMcpConnection") : t("createMcpConnection")}{" "}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
            overflow: "visible",
          }}
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <ComoInput
              placeholder={t("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <ComoInput
              placeholder={t("url")}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              fullWidth
            />

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("authentication")}
              </Typography>
              <ComoSelect
                options={AUTH_TYPES.map((t) => ({
                  id: t.value,
                  label: t.label,
                }))}
                selectedOption={AUTH_TYPES.map((t) => ({
                  id: t.value,
                  label: t.label,
                })).find((opt) => opt.id === authType)}
                onSelect={(option) => setAuthType(Number(option.id))}
                placeholder={t("selectAuthentication")}
              />
            </Box>

            {isEdit && authType !== 0 && (
              <Typography variant="caption" sx={{ color: color.gray600 }}>
                {t("leaveBlankToKeepCurrent")}
              </Typography>
            )}

            {authType === 1 && (
              <>
                <ComoInput
                  placeholder={t("headerName")}
                  value={headerName}
                  onChange={(e) => setHeaderName(e.target.value)}
                  fullWidth
                />
                <ComoInput
                  placeholder={t("apiKey")}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  fullWidth
                  type="password"
                />
              </>
            )}
            {authType === 2 && (
              <ComoInput
                placeholder={t("bearerToken")}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                fullWidth
                type="password"
              />
            )}
            {authType === 3 && (
              <>
                <ComoInput
                  placeholder={t("username")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  fullWidth
                />
                <ComoInput
                  placeholder={t("password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  type="password"
                />
              </>
            )}

            {isEdit && cachedTools.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
                >
                  {t("discoveredTools", {
                    count: cachedTools.length,
                  })}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {cachedTools.map((tool) => (
                    <Chip
                      key={tool.name}
                      label={tool.name}
                      size="small"
                      sx={{
                        bgcolor: color.green50,
                        color: color.green700,
                        fontSize: "0.75rem",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              p: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              borderTop: `1px solid ${color.gray200}`,
            }}
          >
            <ComoButton
              variant="outlined"
              onClick={() => navigate("/mcp_connections")}
            >
              {t("cancel")}
            </ComoButton>
            <ComoButton
              onClick={handleSubmit}
              disabled={
                !name.trim() ||
                !url.trim() ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEdit ? t("update") : t("create")}
            </ComoButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default McpConnectionForm;
